// src/utils/contractorIdentity.js
//
// One place that answers "who is this person, and what are they called?"
//
// WHY THIS EXISTS
// A subcontractor is stored twice. Staff.js writes them to `users` with a
// first and last name, and mirrors them into `contractors` with a company
// name plus a `staffId` pointing back at the user record. Meanwhile a job's
// `techAssign` array stores plain name STRINGS, and a job's `subcontractorPay`
// array stores a plain `subcontractorName` STRING. Nothing carries an id.
//
// So the same human can appear as "Dan Omalley" on an old job, "Omalley
// Fencing LLC" on a new one, and a third spelling on a pay line. Matching on
// any single spelling silently splits their balance.
//
// This module builds ONE identity per person and hangs every name form they
// have ever been called off it as an alias. Callers match against the whole
// alias set instead of guessing which spelling is canonical.
//
// IMPORTANT: this is a read-time merge. Nothing here rewrites stored data, so
// it is safe to adopt page by page and safe to back out.

/* ========================================================================== */
/* Name normalizing — must stay byte-identical to JobEstimateSpreadsheet's     */
/* own normalizePersonName or matching will drift between pages.              */
/* ========================================================================== */

export const normalizePersonName = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Looser still: ignores spacing and punctuation entirely. Used only to WARN
// about near-duplicate spellings, never to match money.
export const looseNameKey = (value = "") =>
  String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

/* ========================================================================== */
/* Worker type                                                                */
/* ========================================================================== */

// Matches Staff.js's "Sub Contractor", plus "subcontractor" and "sub-contractor".
export const isSubcontractor = (person = {}) =>
  String(person.workerType || "")
    .toLowerCase()
    // Strip every separator, not just spaces, so "Sub Contractor",
    // "Sub-Contractor" and "subcontractor" all land in the same place. This is
    // strictly more permissive than the check it replaces, so it can only ever
    // match more rows, never fewer.
    .replace(/[^a-z0-9]+/g, "")
    .includes("subcontractor");

/* ========================================================================== */
/* Display name                                                               */
/* ========================================================================== */

/**
 * The name to SHOW for a person.
 *
 * Subcontractors show their company name, because that is the name on the
 * invoice and the name the check is written to.
 *
 * Everyone else — employees, office staff, anyone without Sub Contractor
 * checked — is completely unaffected and keeps their first and last name.
 * A subcontractor who has no company name on file also keeps their person
 * name, so nobody ever renders as a blank row.
 */
export const getStaffDisplayName = (person = {}) => {
  const personName =
    `${person.firstName || ""} ${person.lastName || ""}`.trim() ||
    person.name ||
    person.displayName ||
    person.email ||
    "";

  if (!isSubcontractor(person)) return personName;

  const company = (person.companyName || "").trim();
  return company || personName;
};

/** Every name form a person might be recorded under, most specific first. */
export const getStaffAliasNames = (person = {}) => [
  person.companyName || "",
  `${person.firstName || ""} ${person.lastName || ""}`.trim(),
  person.name || "",
  person.contactName || "",
  person.displayName || "",
];

/* ========================================================================== */
/* Identities                                                                 */
/* ========================================================================== */

/**
 * Build one identity per subcontractor by joining `users` to `contractors`
 * on staffId.
 *
 * @param {Array} staff             docs from the `users` collection
 * @param {Array} contractorRecords docs from the `contractors` collection
 * @param {boolean} includeTerminated keep Inactive/Terminated contractors
 * @returns {{identities: Array, identityByAlias: Object, resolveIdentity: Function, bucketKeyForName: Function, displayNameFor: Function}}
 */
export const buildContractorIdentities = ({
  staff = [],
  contractorRecords = [],
  includeTerminated = false,
} = {}) => {
  const identities = [];

  const addIdentity = (seed) => {
    const aliasKeys = new Set();
    seed.aliasNames.forEach((n) => {
      const key = normalizePersonName(n);
      if (key) aliasKeys.add(key);
    });
    if (!aliasKeys.size) return;
    identities.push({
      key: seed.key,
      displayName: seed.displayName,
      aliasKeys,
      aliasNames: seed.aliasNames.filter(Boolean),
      ids: new Set(seed.ids.filter(Boolean)),
      staff: seed.staff || null,
      isSubcontractor: true,
    });
  };

  const isRetired = (record) => {
    const status = String(record.status || "Active").toLowerCase();
    return status === "terminated" || status === "inactive";
  };

  const usedStaffIds = new Set();

  // Pass 1 — contractor records, which carry the company name.
  contractorRecords
    .filter((record) => (includeTerminated ? true : !isRetired(record)))
    .forEach((record) => {
      const linkedStaff = record.staffId
        ? staff.find((person) => person.id === record.staffId)
        : null;
      if (linkedStaff) usedStaffIds.add(linkedStaff.id);

      const company = (record.companyName || record.name || "").trim();
      const personName =
        `${record.firstName || ""} ${record.lastName || ""}`.trim() ||
        record.contactName ||
        (linkedStaff ? getStaffAliasNames(linkedStaff)[1] : "") ||
        "";

      addIdentity({
        key: `c:${record.id}`,
        displayName: company || personName,
        aliasNames: [
          company,
          personName,
          record.contactName,
          ...(linkedStaff ? getStaffAliasNames(linkedStaff) : []),
        ],
        ids: [record.staffId, record.id, linkedStaff?.id],
        // Carry the user record so callers can read pay rate and hours.
        staff: linkedStaff,
      });
    });

  // Pass 2 — staff flagged Sub Contractor with no contractor record yet.
  staff
    .filter((person) => isSubcontractor(person) && !usedStaffIds.has(person.id))
    .forEach((person) => {
      addIdentity({
        key: `s:${person.id}`,
        displayName: getStaffDisplayName(person),
        aliasNames: getStaffAliasNames(person),
        ids: [person.id],
        staff: person,
      });
    });

  // First writer wins, so a company name cannot be hijacked by a later record
  // that happens to share a contact name.
  const identityByAlias = {};
  identities.forEach((identity) => {
    identity.aliasKeys.forEach((alias) => {
      if (!identityByAlias[alias]) identityByAlias[alias] = identity;
    });
  });

  const resolveIdentity = (name) => identityByAlias[normalizePersonName(name)] || null;

  // The single key everything buckets by. Roster people key by identity so all
  // their aliases collapse together; unknown names key by their own text so a
  // free-text contractor is still tracked rather than dropped.
  const bucketKeyForName = (name) => {
    const identity = resolveIdentity(name);
    if (identity) return identity.key;
    const loose = normalizePersonName(name);
    return loose ? `n:${loose}` : "";
  };

  // Show the company name for a stored string even when that string is the
  // person's name, without rewriting anything.
  const displayNameFor = (name) => {
    const identity = resolveIdentity(name);
    return identity ? identity.displayName : String(name || "").trim();
  };

  return { identities, identityByAlias, resolveIdentity, bucketKeyForName, displayNameFor };
};

/** True when a techAssign array contains this person under ANY of their names. */
export const techAssignMatches = (techAssign, name, bucketKeyForName) => {
  const target = bucketKeyForName(name);
  if (!target) return false;
  const list = Array.isArray(techAssign) ? techAssign : techAssign ? [techAssign] : [];
  return list.some((entry) => {
    const raw =
      typeof entry === "string" ? entry : entry?.techName || entry?.name || entry?.tech || "";
    return bucketKeyForName(raw) === target;
  });
};
